import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import LocationIcon from "@rsuite/icons/Location";
import { EditSite } from "./Modal/EditSite";
import { UserContext } from "../../_context/UserContext";
import TicketBadge from "../../../../tickets/TicketBadge";
import Card from "../../../../ui/Card/Card";
import Badge from "../../../../ui/Badge/Badge";
import SectionRibbon from "../../../../ui/SectionRibbon/SectionRibbon";
import NoDataState from "../../../../ui/NoDataState/NoDataState";
import Loader from "../../../../ui/Loader/Loader";
import { colors } from "../../../../ui/theme/colors";

export const CustomerSiteListFC = (props) => {
  const [sites, setSites] = useState([]);
  const [activeSites, setActiveSites] = useState([]);
  const [inactiveSites, setInactiveSites] = useState([]);
  const [update, setUpdate] = useState(false);
  const [loadingSites, setLoadingSites] = useState(true);
  const { userRoles } = useContext(UserContext);
  const { getAccessTokenSilently } = useAuth0();

  const getSiteList = async () => {
    setLoadingSites(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const siteRes = await axiosInstance({
        url: `/customer/sites/${props.customer}`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setSites(siteRes?.data[0]);
      setActiveSites(
        siteRes?.data[0].filter((system) => {
          return system.systems > 0;
        }),
      );
      setInactiveSites(
        siteRes?.data[0].filter((system) => {
          return system.systems == 0;
        }),
      );
    } catch (error) {
      console.error(error);
    }
    setLoadingSites(false);
  };

  useEffect(() => {
    getSiteList();
  }, [props.customer, update]);

  if (loadingSites) {
    return (
      <Container>
        <Loader content="Loading sites..." centered variant="glass" />
      </Container>
    );
  }

  return (
    <Container>
      <SectionRibbon
        title="Sites"
        icon={LocationIcon}
        variant="primary"
        rightContent={
          <StatsContainer>
            <StatItem>
              <StatLabel>Active Sites:</StatLabel>
              <StatValue>
                {activeSites?.length} / {sites?.length}
              </StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Total Systems:</StatLabel>
              <StatValue>
                {sites?.reduce((a, b) => a + parseInt(b.systems), 0)}
              </StatValue>
            </StatItem>
          </StatsContainer>
        }
      />

      <SitesSection>
        <SectionLabel>
          <Badge variant="success" size="small">
            ACTIVE SITES
          </Badge>
        </SectionLabel>

        {activeSites.length > 0 ? (
          <SitesGrid>
            {activeSites.map((site, index) => (
              <Card
                key={site.id}
                animated
                visible={true}
                delay={index * 50}
                hoverable={userRoles.includes("superuser")}
                onClick={userRoles.includes("superuser") ? () => {} : undefined}
              >
                <SiteContent>
                  <SiteHeader>
                    <SiteId>#{site.id}</SiteId>
                    <TicketBadge siteId={String(site.id)} />
                    <SystemsBadge variant="primary" size="small">
                      {site.systems} {site.systems === 1 ? "system" : "systems"}
                    </SystemsBadge>
                  </SiteHeader>

                  <SiteName>{site.name || "Unnamed Site"}</SiteName>

                  <SiteDetails>
                    <DetailRow>
                      <DetailLabel>Location:</DetailLabel>
                      <DetailValue>
                        {[site.city, site.state].filter(Boolean).join(", ") ||
                          "N/A"}
                      </DetailValue>
                    </DetailRow>

                    <DetailRow>
                      <DetailLabel>Address:</DetailLabel>
                      <DetailValue>{site.street_address || "N/A"}</DetailValue>
                    </DetailRow>

                    <DetailRow>
                      <DetailLabel>ZIP:</DetailLabel>
                      <DetailValue>{site.zip || "N/A"}</DetailValue>
                    </DetailRow>
                  </SiteDetails>

                  {userRoles.includes("superuser") && (
                    <EditButtonContainer>
                      <EditSite
                        site={site}
                        update={update}
                        setUpdate={setUpdate}
                      />
                    </EditButtonContainer>
                  )}
                </SiteContent>
              </Card>
            ))}
          </SitesGrid>
        ) : (
          <NoDataState
            icon="🏢"
            title="No Active Sites"
            message="No sites with active systems found."
          />
        )}
      </SitesSection>

      {userRoles.includes("superuser") && (
        <SitesSection>
          <SectionLabel>
            <Badge variant="neutral" size="small">
              INACTIVE SITES
            </Badge>
          </SectionLabel>

          {inactiveSites.length > 0 ? (
            <SitesGrid>
              {inactiveSites.map((site, index) => (
                <Card
                  key={site.id}
                  variant="deactivated"
                  animated
                  visible={true}
                  delay={index * 50}
                  hoverable
                >
                  <SiteContent>
                    <SiteHeader>
                      <SiteId>#{site.id}</SiteId>
                      <TicketBadge siteId={String(site.id)} />
                      <SystemsBadge variant="neutral" size="small">
                        0 systems
                      </SystemsBadge>
                    </SiteHeader>

                    <SiteName>{site.name || "Unnamed Site"}</SiteName>

                    <SiteDetails>
                      <DetailRow>
                        <DetailLabel>Location:</DetailLabel>
                        <DetailValue>
                          {[site.city, site.state].filter(Boolean).join(", ") ||
                            "N/A"}
                        </DetailValue>
                      </DetailRow>

                      <DetailRow>
                        <DetailLabel>Address:</DetailLabel>
                        <DetailValue>
                          {site.street_address || "N/A"}
                        </DetailValue>
                      </DetailRow>

                      <DetailRow>
                        <DetailLabel>ZIP:</DetailLabel>
                        <DetailValue>{site.zip || "N/A"}</DetailValue>
                      </DetailRow>
                    </SiteDetails>

                    <EditButtonContainer>
                      <EditSite
                        site={site}
                        update={update}
                        setUpdate={setUpdate}
                      />
                    </EditButtonContainer>
                  </SiteContent>
                </Card>
              ))}
            </SitesGrid>
          ) : (
            <NoDataState
              icon="✅"
              title="No Inactive Sites"
              message="All sites have active systems."
            />
          )}
        </SitesSection>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    align-items: flex-end;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatLabel = styled.span`
  font-size: 14px;
  color: ${colors.neutral[600]};
  font-weight: 500;
`;

const StatValue = styled.span`
  font-size: 16px;
  color: ${colors.neutral[800]};
  font-weight: 600;
`;

const SitesSection = styled.div`
  margin-bottom: 40px;
`;

const SectionLabel = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
`;

const SitesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SiteContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SiteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SiteId = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral[600]};
`;

const SystemsBadge = styled(Badge)`
  flex-shrink: 0;
`;

const SiteName = styled.h4`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.neutral[800]};
  line-height: 1.4;
`;

const SiteDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const DetailLabel = styled.span`
  font-size: 13px;
  color: ${colors.neutral[500]};
  font-weight: 500;
  min-width: 70px;
  flex-shrink: 0;
`;

const DetailValue = styled.span`
  font-size: 13px;
  color: ${colors.neutral[700]};
  line-height: 1.4;
`;

const EditButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
`;
